import { createContext, useContext, useId, type AriaAttributes, type FieldsetHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface FormFieldContextValue {
  id: string
  describedBy?: string
  invalid?: boolean
}

const FormFieldContext = createContext<FormFieldContextValue | undefined>(undefined)

/** Used by Input, Textarea and SelectTrigger; avoids DOM traversal or cloning children. */
export function useFormFieldControl(props: AriaAttributes = {}) {
  const field = useContext(FormFieldContext)
  return field ? {
    id: field.id,
    'aria-describedby': [props['aria-describedby'], field.describedBy].filter(Boolean).join(' ') || undefined,
    'aria-invalid': field.invalid || props['aria-invalid'] || undefined,
  } : {}
}

interface FormFieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode
  children: ReactNode
  description?: ReactNode
  hint?: ReactNode
  error?: ReactNode
}

/** Wrap one control; put any explicit id here. For groups use a fieldset and individual labels. */
export function FormField({ id, label, children, description, hint, error, className, ...props }: FormFieldProps) {
  const generatedId = useId()
  const controlId = id ?? `field-${generatedId}`
  const helpText = description ?? hint
  const descriptionId = helpText ? `${controlId}-description` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined

  return <FormFieldContext.Provider value={{ id: controlId, describedBy, invalid: !!error }}>
    <div className={cn('space-y-2', className)} {...props}>
      <Label htmlFor={controlId}>{label}</Label>
      {children}
      {helpText && <p id={descriptionId} className="text-sm text-muted-foreground">{helpText}</p>}
      {error && <p id={errorId} role="alert" className="text-sm text-destructive">{error}</p>}
    </div>
  </FormFieldContext.Provider>
}


/** Lock editable native controls as one group while a submitted snapshot saves. */
export function FormFields({ className, disabled, ...props }: FieldsetHTMLAttributes<HTMLFieldSetElement>) {
  return <fieldset className={cn('contents', className)} disabled={disabled} aria-busy={disabled || undefined} {...props} />
}
